import { useState, useCallback, useRef } from 'react';
import { FetchElements } from "../../../../../utils/js/request";
import { useNavigate } from "react-router-dom";
import { REQUEST } from "../../../../../utils/js/Const";

export const useSearchGoods = () => {
    const [goods, setGoods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const currentOffsetRef = useRef(0);
    const currentTextRef = useRef('');
    const navigate = useNavigate();
    const limit = 20;

    const searchGoods = useCallback(async (text = '', offset = 0, append = false) => {

        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            setGoods([]);
            currentOffsetRef.current = 0;
        }
        setError(null);

        let url = `${REQUEST}/api/v1/goods/search`;
        const params = new URLSearchParams();

        if (text && text.trim()) {
            params.append('text', text.trim());
        }
        params.append('limit', limit);
        params.append('offset', offset);

        const queryString = params.toString();
        if (queryString) {
            url += `?${queryString}`;
        }

        return new Promise((resolve, reject) => {
            FetchElements(
                url,
                "GET",
                (responseData) => {
                    const items = responseData?.data || [];
                    const total = responseData?.quantity || 0;

                    if (append) {
                        setGoods(prev => [...prev, ...items]);
                    } else {
                        setGoods(items);
                    }

                    setTotalCount(total);

                    let hasMoreItems;
                    if (total > 0) {
                        const newOffset = offset + limit;
                        hasMoreItems = newOffset < total;
                    } else {
                        hasMoreItems = items.length === limit;
                    }

                    setHasMore(hasMoreItems);

                    if (append) {
                        setLoadingMore(false);
                    } else {
                        setLoading(false);
                    }
                    resolve(responseData);
                },
                navigate,
                2,
                () => navigate("/auth"),
                (title, message, statusCode) => {
                    const errorObj = { title, message, statusCode };
                    setError(errorObj);
                    if (append) {
                        setLoadingMore(false);
                    } else {
                        setLoading(false);
                    }
                    reject(errorObj);
                    console.error(`${title}: ${message}`);
                },
                0,
                null,
                [],
                false,
                true
            );
        });
    }, [navigate, limit]);

    const loadMore = useCallback(async () => {
        if (loadingMore || !hasMore) {
            return;
        }
        const nextOffset = currentOffsetRef.current + limit;
        currentOffsetRef.current = nextOffset;
        return searchGoods(currentTextRef.current, nextOffset, true);
    }, [loadingMore, hasMore, searchGoods, limit]);

    const search = useCallback(async (text = '') => {
        currentTextRef.current = text;
        currentOffsetRef.current = 0;
        setHasMore(true);
        setGoods([]);
        return searchGoods(text, 0, false);
    }, [searchGoods]);

    const clearResults = useCallback(() => {
        setGoods([]);
        setTotalCount(0);
        setError(null);
        setHasMore(true);
        currentOffsetRef.current = 0;
        currentTextRef.current = '';
    }, []);

    return {
        search,
        loadMore,
        goods,
        loading,
        loadingMore,
        error,
        totalCount,
        quantity: totalCount,
        hasMore,
        clearResults
    };
};